import { useCallback, useEffect, useState, useRef } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { useGroupData, useImageOrganizeContext } from '../../../views/imageBrowse/hooks'
import useViewMode from '../../../views/imageBrowse/hooks/useViewMode'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

export default function useWatchViewModeForFocusedIndex({ ribbonImages }) {
  const [savedOffset, setSavedOffset] = useState(-1)
  const { viewMode } = useViewMode()
  const { query } = useQueryParams()
  const { dividerOffsets } = useImageOrganizeContext()
  // Track if we loaded with cat param (viewMode might still be 'single' on first render)
  const initialLoadWithCat = useRef(query?.cat)

  const { itemNumber } = useGroupData()

  const sharedBrowserState = useSharedBrowserStateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  // we don't use useSetFocusedIndex because we don't want the ark to be updated when the user switches between views
  const setFocusedIndex = useCallback(
    (value) => sharedBrowserDispatch({ type: 'SET_FOCUSED_INDEX', value }),
    [sharedBrowserDispatch]
  )

  // Watches for toggling between group and single view mode. Saves the offset and then subtracts or adds it
  // when the user switches between views
  // need useEffect ribbonImages updates which relies on zionSlsState
  useEffect(() => {
    // Special case: Initial load with cat param in group mode
    // The focused index is already correct from ARK, just save the offset for switching to single later
    if (
      initialLoadWithCat.current &&
      query?.cat &&
      viewMode === 'group' &&
      savedOffset < 0 &&
      dividerOffsets?.length > 0
    ) {
      if (dividerOffsets[itemNumber - 1] > -1) {
        setSavedOffset(dividerOffsets[itemNumber - 1])
      }
      return
    }

    // Clear the initial load flag once we've handled it or if cat is removed
    if (initialLoadWithCat.current && (!query?.cat || savedOffset >= 0)) {
      initialLoadWithCat.current = false
    }

    if (
      viewMode === 'group' &&
      savedOffset < 0 &&
      dividerOffsets?.length > 0 &&
      dividerOffsets[itemNumber - 1] > -1 &&
      (sharedBrowserState?.focusedIndex || 0) + dividerOffsets[itemNumber - 1] > -1 &&
      ribbonImages?.length > (sharedBrowserState?.focusedIndex || 0) + dividerOffsets[itemNumber - 1]
    ) {
      setFocusedIndex(sharedBrowserState.focusedIndex + dividerOffsets[itemNumber - 1])
      setSavedOffset(dividerOffsets[itemNumber - 1])
    } else if (viewMode === 'single' && savedOffset > 0) {
      if (sharedBrowserState.focusedIndex - savedOffset > 0)
        setFocusedIndex(sharedBrowserState.focusedIndex - savedOffset)
      else setFocusedIndex(0)
      setSavedOffset(-1)
    }
  }, [itemNumber, dividerOffsets, ribbonImages, savedOffset, setFocusedIndex, sharedBrowserState, viewMode, query?.cat])
}
